import React, { useEffect, useMemo, useState } from "react";
import {
  DashboardPageWrapper,
  Toolbar,
  Title,
  ContentArea,
  MainContent,
  ChartsGrid,
  ChartContainer,
  ChartTitle,
  FiltersWrapper,
} from "./styles";
import { KpiSection } from "./sections";
import api from "../../network/api";
import { Statistics2 } from "../../types/Statistics2";
import { CircularProgress } from "@mui/material";
import { LoadingText } from "../../components/data/styles";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import colors from "../../styles/colors";

const StatisticsPage: React.FC = () => {
  const { t, i18n } = useTranslation();

  const [stats, setStats] = useState<Statistics2 | null>(null);
  const [loading, setLoading] = useState(false);

  const monthOptions = useMemo(() => {
    return Array.from({ length: 3 }, (_, i) => {
      const d = new Date();
      d.setDate(1);
      d.setMonth(d.getMonth() - i);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleString(i18n.language, {
        month: "long",
        year: "numeric",
      });
      return { value, label };
    });
  }, [i18n.language]);

  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].value);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      const data = await api.getStatistics2({ month: selectedMonth });
      setStats(data);
      setLoading(false);
    };
    fetchStats();
  }, [selectedMonth]);

  if (loading) {
    return (
      <DashboardPageWrapper>
        <Toolbar>
          <Title style={{ margin: "0 auto" }}>
            {t("ADVANCED_TRANSACTION_ANALYTICS")}
          </Title>
        </Toolbar>
        <ContentArea
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
          <LoadingText>{t("LOADING_DATA")}</LoadingText>
        </ContentArea>
      </DashboardPageWrapper>
    );
  }

  if (!stats) return null;

  return (
    <DashboardPageWrapper>
      <Toolbar>
        <FiltersWrapper>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            style={{
              background: "transparent",
              color: "white",
              border: "1px solid rgba(255,255,255,0.4)",
              borderRadius: 6,
              padding: "4px 8px",
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            {monthOptions.map((m) => (
              <option key={m.value} value={m.value} style={{ color: "black" }}>
                {m.label}
              </option>
            ))}
          </select>
        </FiltersWrapper>
        <Title>{t("ADVANCED_TRANSACTION_ANALYTICS")}</Title>
      </Toolbar>

      <ContentArea>
        <MainContent>
          {/* KPI cards — single compact row */}
          <KpiSection
            overview={stats.overview}
            subscriptionStats={stats.subscriptionStats}
            pagesByDay={stats.pagesByDay}
          />

          <ChartsGrid>
            {/* Pages per day — for the selected month */}
            <ChartContainer>
              <ChartTitle>{t("PAGES_PER_DAY")}</ChartTitle>
              {stats.pagesByDay.length === 0 ? (
                <div
                  style={{
                    height: 250,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: colors.theme.galilGrey,
                    fontSize: "0.9rem",
                  }}
                >
                  {t("NO_DATA_FOR_MONTH")}
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart
                    data={stats.pagesByDay.map((d) => ({
                      ...d,
                      not_success:
                        (d.failed || 0) +
                        (d.exception || 0) +
                        (d.calc_error || 0),
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="success"
                      stackId="pages"
                      fill={colors.theme.galilGreen}
                      name={t("PAGES_SUCCESS")}
                    />
                    <Bar
                      dataKey="not_success"
                      stackId="pages"
                      fill={colors.theme.galilOrange}
                      name={t("PAGES_FAILED")}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartContainer>
          </ChartsGrid>
        </MainContent>
      </ContentArea>
    </DashboardPageWrapper>
  );
};

export default StatisticsPage;
